import { safeLocalStorage } from '@/lib/safeStorage';
import React, {useState} from 'react';
import {Button} from '../../components/ui/button';
import {Input} from '../../components/ui/input';
import {Label} from '../../components/ui/label';
import {InputOTP, InputOTPGroup, InputOTPSlot} from '../../components/ui/input-otp';
import { ArrowLeft, CheckCircle, CircleNotch, Envelope } from '@phosphor-icons/react';
import {confirmRegistration, resendConfirmationCode} from '../../lib/api';
import {useNavigate} from 'react-router-dom';
import {usePageTitle} from '../../hooks/usePageTitle';
import {Glow, SetupShell, StepActions, journeyEyebrow, journeyField, journeyLabel, journeyStepBody} from '../../components/onboarding/SetupShell';
import {cn} from '../../lib/utils';

const REGISTRATION_STORAGE_KEY = 'ruta_registration_state';

/**
 * The code screen of step 1, reached from the link in the email rather than
 * from the signup page — same shell, same list of steps, so it reads as the
 * same thing (RUT-4518).
 */
const AuthShell: React.FC<{
  title: string;
  description?: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <SetupShell eyebrow={journeyEyebrow(1)} title={title} lead={description} current={1}>
    {children}
  </SetupShell>
);

const ConfirmEmail: React.FC = () => {
  usePageTitle('Confirm Email');
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [autoConfirming, setAutoConfirming] = useState(false);

  // Handle confirmation links from email
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    const codeParam = urlParams.get('confirmation_code') || urlParams.get('code');

    // Check for stored registration data (from recent registration)
    try {
      const storedData = safeLocalStorage.getItem(REGISTRATION_STORAGE_KEY);
      if (storedData) {
        const registrationData = JSON.parse(storedData);
        if (registrationData.email && !emailParam) {
          setEmail(registrationData.email);
        }
      }
    } catch (err) {
      console.error('Error loading registration data:', err);
    }

    if (emailParam && codeParam) {
      setEmail(emailParam);
      setConfirmationCode(codeParam);
      setAutoConfirming(true);

      // Auto-submit the confirmation
      handleAutoConfirm(emailParam, codeParam);
    }
  }, []);

  const handleAutoConfirm = async (emailVal: string, codeVal: string) => {
    setSubmitting(true);
    setError(null);
    setInfo(null);

    try {
      // No password available for users coming from email link
      const result = await confirmRegistration(emailVal, codeVal);

      setSuccess(true);

      // If we got auth tokens, user is logged in
      if (result.authenticationResult) {
        // Signed in: the setup steps (plan, import) draw on the same page as signup
        setTimeout(() => {
          navigate('/onboarding');
        }, 1500);
      } else {
        // Auto-redirect to login; flag triggers a persistent toast on the login page
        setTimeout(() => {
          navigate('/login?emailConfirmed=1');
        }, 3000);
      }

    } catch (err: any) {
      setError(err.message || 'Failed to confirm email from link');
      setAutoConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!confirmationCode.trim()) {
      setError('Please enter the confirmation code');
      return;
    }

    setSubmitting(true);
    setError(null);
    setInfo(null);

    try {
      // No password available when confirming manually
      const result = await confirmRegistration(email.trim(), confirmationCode.trim());

      setSuccess(true);

      // If we got auth tokens, user is logged in
      if (result.authenticationResult) {
        // Signed in: the setup steps (plan, import) draw on the same page as signup
        setTimeout(() => {
          navigate('/onboarding');
        }, 1500);
      } else {
        // Auto-redirect to login; flag triggers a persistent toast on the login page
        setTimeout(() => {
          navigate('/login?emailConfirmed=1');
        }, 3000);
      }

    } catch (err: any) {
      setError(err.message || 'Failed to confirm email');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email.trim()) {
      setError('Please enter your email address first');
      return;
    }

    setResending(true);
    setError(null);
    setInfo(null);

    try {
      await resendConfirmationCode(email.trim());
      setInfo('A new confirmation code has been sent to your email.');
    } catch (err: any) {
      setError(err.message || 'Failed to resend confirmation code');
    } finally {
      setResending(false);
    }
  };

  if (autoConfirming && submitting) {
    return (
      <div className="min-h-screen bg-nav flex items-center justify-center relative overflow-hidden">
        <Glow />
        <div className="text-center relative z-10">
          <CircleNotch className="w-8 h-8 animate-spin mx-auto mb-4 text-white" />
          <p className="text-white/70">Confirming your email...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <AuthShell
        title="Email confirmed"
        description="Your email has been verified. Next: your price."
      >
        <div className="py-2">
          <CheckCircle className="mb-4 h-16 w-16 text-primary" weight="fill" />
          <p className="mb-6 text-base text-stone-600">
            You can now log in to your account.
          </p>

          <Button
            onClick={() => navigate('/login?emailConfirmed=1')}
            className="h-14 px-8 text-lg font-semibold"
          >
            Go to log in
          </Button>

          <p className="mt-4 text-sm text-muted-foreground">
            Redirecting automatically...
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Check your email"
      description="Enter the 6-digit code we sent you."
    >
      {error && (
        <div className="mb-8 rounded-lg bg-destructive/10 p-4 text-base text-destructive" role="alert">
          {error}
        </div>
      )}
      {info && (
        <div className="mb-8 rounded-lg bg-primary/10 p-4 text-base text-primary" role="status">
          {info}
        </div>
      )}

      <form onSubmit={handleConfirm} className={`space-y-8 ${journeyStepBody}`}>
        <div>
          <Label htmlFor="email" className={journeyLabel}>
            Email address
          </Label>
          <div className="relative">
            <Envelope className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="you@email.com"
              className={cn(journeyField, 'pl-12')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="confirmationCode" className="mb-4 block text-base font-medium text-stone-700">
            Confirmation code
          </Label>
          <InputOTP
            maxLength={6}
            value={confirmationCode}
            onChange={(value) => setConfirmationCode(value.replace(/\D/g, '').slice(0, 6))}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} className="h-14 w-12 border-stone-300 text-xl" />
              <InputOTPSlot index={1} className="h-14 w-12 border-stone-300 text-xl" />
              <InputOTPSlot index={2} className="h-14 w-12 border-stone-300 text-xl" />
              <InputOTPSlot index={3} className="h-14 w-12 border-stone-300 text-xl" />
              <InputOTPSlot index={4} className="h-14 w-12 border-stone-300 text-xl" />
              <InputOTPSlot index={5} className="h-14 w-12 border-stone-300 text-xl" />
            </InputOTPGroup>
          </InputOTP>
          <p className="mt-4 text-base text-muted-foreground">
            Enter the 6-digit code we sent to your email. Don't see it? Check your spam folder — it should arrive within a minute or two.
          </p>
        </div>

        <Button
          type="button"
          variant="link"
          onClick={handleResend}
          disabled={resending || !email.trim()}
          className="h-auto p-0 text-base"
        >
          {resending ? 'Sending...' : "Didn't get it? Resend code"}
        </Button>

        <StepActions
          secondary={
            <Button type="button" variant="secondary" onClick={() => navigate('/login')} className="h-12 px-5 text-base">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to log in
            </Button>
          }
          primary={
            <Button type="submit" disabled={submitting || !email.trim() || confirmationCode.length !== 6} className="h-12 px-7 text-base font-semibold">
              {submitting ? <CircleNotch className="mr-2 h-5 w-5 animate-spin" /> : null}
              {submitting ? 'Confirming...' : 'Verify & continue'}
            </Button>
          }
        />
      </form>
    </AuthShell>
  );
};

export default ConfirmEmail;
